import React, { useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Drawer, IconButton, Link, Skeleton, Typography } from "@mui/material";
import { RiCloseLine } from "@remixicon/react";
import { focusRing, tokens } from "../../utils/figmaTokens";
import { dayTimeLabel, egp } from "../../utils/queuePresentation";
import { countText, dateTimeLines } from "../../utils/kitPresentation";
import { hoursText } from "../../utils/tutorPresentation";
import { durationLabel, learnerName, personName } from "../../utils/studentDetail";
import { cycleEndedLabel, tutorCycleLessons, tutorCyclePayments } from "../../utils/cyclePay";
import InitialsAvatar from "../../components/kit/InitialsAvatar";
import DetailRows from "../../components/kit/DetailRows";
import StatusChip from "../../components/kit/StatusChip";
import SmallButton from "../../components/kit/SmallButton";
import { useUsersByIds } from "../Students/tabs/useUsersByIds";
import { useChildNames } from "../Tutors/tabs/useChildNames";

// Payout detail (044 D119–D122), Figma 11362:28028: one tutor's pay for one cycle, in a side panel
// the link carries (`?cycle=&tutor=`, D120). Figma's platform fee, dispute banner, bank method and
// Put on hold have no data here (D121). Mark as paid opens 053's pay dialog and Undo the undo
// dialog; the page owns both (D122). `busy` is true while the page's rows reload. The learner
// names are read only for the lessons shown.
const LATEST = 5;
const cardSx = { border: `1px solid ${tokens.border.default}`, borderRadius: "16px", p: "16px 20px" };
const lineSx = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", py: "10px", borderTop: `1px solid ${tokens.border.default}` };

const counted = (n, one, many) => `${countText(n)} ${n === 1 ? one : many}`;

// D121's Breakdown: the late cancels, the fixed pay and Paid so far only when there are any.
const breakdownRows = (row) => {
  const { taught, lateCancels } = row.breakdown;
  return [
    { key: "taught", label: `${counted(taught.count, "lesson", "lessons")} taught · ${hoursText(taught.hours)}`, value: egp(taught.pay) },
    lateCancels.count > 0 && {
      key: "late",
      label: `${counted(lateCancels.count, "late cancel", "late cancels")} paid · ${hoursText(lateCancels.hours)}`,
      value: egp(lateCancels.pay),
    },
    row.fixed > 0 && { key: "fixed", label: "Fixed monthly pay", value: egp(row.fixed) },
    { key: "total", label: "Total", value: egp(row.total) },
    row.paid > 0 && { key: "paid", label: "Paid so far", value: egp(row.paid) },
    { key: "left", label: "Left to pay", value: egp(row.status.left) },
  ].filter(Boolean);
};

const PanelBody = ({ row, busy, cycle, bookings, payments, canPay, canUndo, canOpenTutor, onClose, onPay, onUndo, onReceipt }) => {
  const name = row.tutor.name || row.tutor.id;
  const left = row.status.left;
  const lessons = tutorCycleLessons(bookings, row.tutor);
  const latest = lessons.slice(0, LATEST);
  const usersById = useUsersByIds(latest.map((l) => l.booking.studentId));
  const childrenById = useChildNames(latest.map((l) => l.booking));
  const paidList = tutorCyclePayments(payments, row.tutor.id);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", p: "20px 24px", borderBottom: `1px solid ${tokens.border.default}` }}>
        <InitialsAvatar name={name} colorKey={row.tutor.id} size={40} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="payout-panel-title" variant="titleSection" component="h2" noWrap sx={{ color: tokens.text.primary }}>
            {name}
          </Typography>
          <Typography variant="labelMedium" component="div" sx={{ color: tokens.text.tertiary }}>
            Payout · {cycle.label}
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} disableFocusRipple sx={{ color: tokens.icon.default, ...focusRing }}>
          <RiCloseLine size={20} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: "16px", sm: "20px 24px" }, display: "flex", flexDirection: "column", gap: "16px" }}>
        <Box sx={{ borderRadius: "16px", bgcolor: tokens.brand.subtle, p: "16px 20px", display: "flex", flexDirection: "column", gap: "4px" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
            <Typography variant="titleMetric" component="div" sx={{ color: tokens.brand.text }}>
              {egp(left > 0 ? left : row.total)}
            </Typography>
            <StatusChip label={row.status.label} color={row.status.color} />
          </Box>
          <Typography variant="labelMedium" component="div" sx={{ color: tokens.brand.text }}>
            {left > 0 ? "Left to pay" : "Paid in full"} · {cycle.label}, ended {cycleEndedLabel(cycle)}
          </Typography>
        </Box>

        <Box component="section" aria-labelledby="payout-breakdown-title" sx={cardSx}>
          <Typography id="payout-breakdown-title" variant="titleCard" component="h3" sx={{ color: tokens.text.primary }}>
            Breakdown
          </Typography>
          <DetailRows rows={breakdownRows(row)} />
        </Box>

        <Box component="section" aria-labelledby="payout-lessons-title" sx={cardSx}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", pb: "6px" }}>
            <Typography id="payout-lessons-title" variant="titleCard" component="h3" sx={{ color: tokens.text.primary }}>
              Lessons in this cycle
            </Typography>
            {canOpenTutor && lessons.length > 0 && (
              // The kit ViewAllLink's look, without its card-foot rule: this one sits in the head.
              <Link
                component={RouterLink}
                to={`/tutors/${row.tutor.id}/sessions`}
                underline="hover"
                variant="bodySmallStrong"
                sx={{ color: tokens.brand.text, whiteSpace: "nowrap" }}
              >
                View all {countText(lessons.length)}
              </Link>
            )}
          </Box>
          {bookings === null ? (
            <Skeleton width="60%" />
          ) : latest.length === 0 ? (
            <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
              No lesson pays in this cycle.
            </Typography>
          ) : (
            latest.map(({ booking: b, pay, lateCancel }) => {
              // As the tutor's Sessions tab names a learner: the child, or the student ("-" when
              // the student cannot be read, as SessionsTable shows it).
              const learner =
                b.learner?.kind === "child" ? learnerName(b, childrenById) : personName(usersById[b.studentId]) || "-";
              return (
                <Box key={b.id} sx={lineSx}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="bodySmallStrong" component="div" noWrap sx={{ color: tokens.text.primary }}>
                      {learner}
                    </Typography>
                    <Typography variant="labelMedium" component="div" sx={{ color: tokens.text.tertiary }}>
                      {b.subject || "Lesson"} · {durationLabel(b.duration)}
                      {lateCancel ? " · late cancel" : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: "12px", flexShrink: 0 }}>
                    <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
                      {dateTimeLines(b.startTimeUTC)?.date || "—"}
                    </Typography>
                    <Typography variant="bodySmallStrong" sx={{ color: tokens.text.primary }}>
                      {egp(pay)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        <Box component="section" aria-labelledby="payout-payments-title" sx={cardSx}>
          <Typography id="payout-payments-title" variant="titleCard" component="h3" sx={{ color: tokens.text.primary, pb: "6px" }}>
            Payments for this cycle
          </Typography>
          {payments === null ? (
            <Skeleton width="60%" />
          ) : paidList.length === 0 ? (
            <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
              No payment recorded for this cycle yet.
            </Typography>
          ) : (
            paidList.map((p) => (
              <Box key={p.id} sx={lineSx}>
                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
                  <Typography variant="bodySmall" sx={{ color: tokens.text.primary }}>
                    {dayTimeLabel(p.createdAt)} · {egp(p.amount)}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {p.receiptPath ? (
                      <Link component="button" type="button" onClick={() => onReceipt(p.receiptPath)}>
                        {p.receiptName || "Receipt"}
                      </Link>
                    ) : (
                      <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
                        {p.paymentReference || "No receipt"}
                      </Typography>
                    )}
                    {p.undoneAt && <StatusChip label="Undone" color="default" />}
                  </Box>
                </Box>
                {canUndo && !p.undoneAt && (
                  <SmallButton tone="outline" onClick={() => onUndo(p, name)}>
                    Undo
                  </SmallButton>
                )}
              </Box>
            ))
          )}
        </Box>
      </Box>

      {canPay && left > 0 && (
        <Box sx={{ p: "16px 24px", borderTop: `1px solid ${tokens.border.default}` }}>
          {/* While the rows reload after a payment or an undo, `row` is the last one: wait for
              the new figure (R67). */}
          <Button
            fullWidth
            variant="contained"
            disabled={busy}
            onClick={() => onPay(row)}
            sx={{ py: "10px", fontSize: 15, fontWeight: 500 }}
          >
            Mark as paid
          </Button>
        </Box>
      )}
    </>
  );
};

// The panel keeps showing its last row while it slides out, so it never closes empty.
const PayoutPanel = ({ row, onClose, ...rest }) => {
  const last = useRef(row);
  if (row) last.current = row;
  const shown = row || last.current;
  return (
    <Drawer
      anchor="right"
      open={Boolean(row)}
      onClose={onClose}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": "payout-panel-title",
          sx: { width: { xs: "100%", sm: 480 }, display: "flex", flexDirection: "column" },
        },
      }}
    >
      {shown && <PanelBody row={shown} onClose={onClose} {...rest} />}
    </Drawer>
  );
};

export default PayoutPanel;
